import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  ArrowLeft,
  Eye,
  Users,
  Activity,
  Flame,
  Search,
  Inbox,
  Download,
  Clock,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Papa from "papaparse";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Pagination, usePagination } from "../components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CHART_COLORS,
  opensByDay,
  opensByHour,
  openCountsByRecipient,
  engagementBuckets,
} from "../lib/analytics";
import { loadContacts, toNameMap, resolveName } from "../lib/contacts";

const API_BASE_URL = "https://email-backend.heybuddy.co.in";

const chartTooltip = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0.5rem",
  color: "hsl(var(--card-foreground))",
  fontSize: "0.8rem",
};

const nf = (n) => (n ?? 0).toLocaleString();

const downloadCsv = (rows, filename) => {
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const StatCard = ({ title, value, icon: Icon, hint }) => (
  <Card>
    <CardContent className="flex items-start justify-between p-5">
      <div className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
        <Icon className="h-5 w-5" />
      </div>
    </CardContent>
  </Card>
);

const ReportPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [minOpens, setMinOpens] = useState("all");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [rep, camp] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/reports/${campaignId}`).catch((e) => {
          if (e.response?.status !== 404) toast.error("Failed to fetch report data");
          return { data: null };
        }),
        axios.get(`${API_BASE_URL}/api/campaigns/${campaignId}`).catch(() => ({ data: null })),
      ]);
      if (!active) return;
      setReport(rep.data);
      setCampaign(camp.data);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [campaignId]);

  const details = useMemo(() => report?.opens?.details || [], [report]);
  const counts = useMemo(() => openCountsByRecipient(details), [details]);

  // Send-time recipient names (if persisted) take priority over the master list.
  const nameMap = useMemo(
    () => ({ ...loadContacts(), ...toNameMap(campaign?.recipients) }),
    [campaign]
  );

  const stats = useMemo(() => {
    const entries = Object.entries(counts);
    const totalOpens = report?.opens?.total || details.length;
    const unique = entries.length;
    const most = entries.reduce(
      (acc, [email, c]) => (c > acc.count ? { email, count: c } : acc),
      { email: "—", count: 0 }
    );
    return {
      totalOpens,
      unique,
      avg: unique ? (totalOpens / unique).toFixed(1) : "0",
      most,
      repeat: entries.filter(([, c]) => c > 1).length,
    };
  }, [counts, report, details]);

  const timeSeries = useMemo(() => opensByDay(details), [details]);
  const hourly = useMemo(() => opensByHour(details), [details]);
  const buckets = useMemo(() => engagementBuckets(counts), [counts]);

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minOpens === "all" ? 0 : Number(minOpens);
    return Object.entries(counts)
      .filter(([email, c]) => (!q || email.toLowerCase().includes(q)) && c >= min)
      .sort((a, b) => b[1] - a[1]);
  }, [counts, search, minOpens]);

  const { page, setPage, pageCount, total, pageSize, pageItems } =
    usePagination(filteredEntries, 10);

  const exportSegment = () => {
    if (filteredEntries.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const rows = filteredEntries.map(([email, opens]) => ({
      name: resolveName(email, nameMap),
      email,
      opens,
      campaign: campaign?.name || "",
      category: campaign?.category || "",
    }));
    downloadCsv(rows, `contacts_${campaign?.name || "campaign"}_opens.csv`);
    toast.success(`${rows.length} contacts exported`);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-28 w-full" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="space-y-6">
        <Button variant="outline" size="sm" onClick={() => navigate("/campaigns")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to campaigns
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="font-medium">No report yet</p>
            <p className="text-sm text-muted-foreground">
              This campaign has no tracked opens so far.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate("/campaigns")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h2 className="truncate text-xl font-semibold">
            {campaign?.name || "Campaign Report"}
          </h2>
          <p className="text-sm text-muted-foreground">Open &amp; engagement analytics</p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={exportSegment}>
          <Download className="mr-2 h-4 w-4" /> Export
        </Button>
      </div>

      {/* Details */}
      {campaign && (
        <Card>
          <CardContent className="grid grid-cols-2 gap-4 p-6 lg:grid-cols-4">
            {[
              { k: "Subject", v: campaign.subject },
              {
                k: "Category",
                v: campaign.category ? (
                  <Badge variant="outline">{campaign.category}</Badge>
                ) : (
                  "—"
                ),
              },
              {
                k: "Status",
                v: <Badge className="capitalize">{campaign.status || "—"}</Badge>,
              },
              {
                k: "Created",
                v: campaign.createdAt
                  ? new Date(campaign.createdAt).toLocaleDateString()
                  : "—",
              },
            ].map((item) => (
              <div key={item.k} className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {item.k}
                </p>
                <div className="mt-1 truncate font-medium">{item.v}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Total Opens" value={nf(stats.totalOpens)} icon={Eye} />
        <StatCard title="Unique Openers" value={nf(stats.unique)} icon={Users} />
        <StatCard title="Avg Opens / Contact" value={stats.avg} icon={Activity} />
        <StatCard
          title="Most Active"
          value={nf(stats.most.count)}
          icon={Flame}
          hint={stats.most.email}
        />
      </div>

      {/* Opens over time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Opens Over Time</CardTitle>
          <p className="text-sm text-muted-foreground">When this campaign was opened</p>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            {timeSeries.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeSeries} margin={{ left: -18, right: 8, top: 4 }}>
                  <defs>
                    <linearGradient id="rptGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={chartTooltip} />
                  <Area type="monotone" dataKey="opens" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#rptGrad)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Hour of day + engagement depth */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Opens by Hour</CardTitle>
            <Clock className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full min-w-0">
              {details.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourly} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} interval={2} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={chartTooltip} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                    <Bar dataKey="opens" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Engagement Depth</CardTitle>
            <p className="text-sm text-muted-foreground">Contacts grouped by number of opens</p>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full min-w-0">
              {stats.unique === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={buckets} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={chartTooltip} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                      {buckets.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed table */}
      <Card>
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">Contact Engagement</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search recipients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={minOpens} onValueChange={setMinOpens}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All opens</SelectItem>
                <SelectItem value="2">Opened ≥ 2×</SelectItem>
                <SelectItem value="4">Opened ≥ 4×</SelectItem>
                <SelectItem value="6">Opened ≥ 6×</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Recipient</TableHead>
                  <TableHead className="text-right">Opens</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={2} className="py-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                          <Inbox className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-medium">
                          {search || minOpens !== "all"
                            ? "No matching recipients"
                            : "No opens recorded yet"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map(([email, count]) => {
                    const name = resolveName(email, nameMap);
                    return (
                      <TableRow key={email}>
                        <TableCell>
                          {name ? (
                            <div className="min-w-0">
                              <div className="font-medium">{name}</div>
                              <div className="text-xs text-muted-foreground">{email}</div>
                            </div>
                          ) : (
                            <span className="font-medium">{email}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={count > 1 ? "default" : "secondary"}>{count}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {total > 0 && (
            <div className="border-t">
              <Pagination page={page} pageCount={pageCount} total={total} pageSize={pageSize} onPageChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
    <Activity className="mb-2 h-8 w-8 opacity-40" />
    <p className="text-sm">No data yet</p>
  </div>
);

export default ReportPage;
